import React, { useMemo, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOcrInputStore } from '../store/ocrInputStore';
import { ExtractedData, ValueObject } from '../types/dataInputTypes';

const isValueObject = (obj: any): obj is ValueObject => {
  return obj && typeof obj === 'object' && 'value' in obj;
};

const EditableCell: React.FC<{ data: any; path: (string | number)[] }> = React.memo(({ data, path }) => {
  const { updateExtractedValueByPath } = useOcrInputStore();

  if (!isValueObject(data)) {
    return <TableCell />;
  }
  
  const score = data.confidence_score;
  const borderColor = score < 80 ? 'error.main' 
                    : score < 95 ? 'warning.main' 
                    : 'success.main';
  
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateExtractedValueByPath(path, e.target.value);
  }, [path, updateExtractedValueByPath]);
  
  return (
    <TableCell>
      <TextField
        fullWidth variant="outlined" size="small"
        value={data.value ?? ''}
        onChange={handleChange}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& .MuiOutlinedInput-notchedOutline': { borderColor, borderWidth: '2px' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor },
          },
        }}
      />
    </TableCell>
  );
});

const SimpleKeyValueTable: React.FC<{ title: string; data: ValueObject; basePath: (string | number)[] }> = React.memo(({ title, data, basePath }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
    <Table size="small">
      <TableBody>
        <TableRow>
          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '30%' }}>
            {title.replace(/_/g, ' ')}
          </TableCell>
          <EditableCell data={data} path={[...basePath, title]} />
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
));

const ComplexTable: React.FC<{ rows: ExtractedData; columns: string[]; basePath: (string | number)[] }> = React.memo(({ rows, columns, basePath }) => {
  const { t } = useTranslation();
  return (
  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
    <Table stickyHeader size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 'bold', minWidth: 150, zIndex: 10 }}>{t('ocrDataInput.field', 'Field')}</TableCell>
          {columns.map(colKey => (
            <TableCell key={colKey} sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
              {colKey.replace(/_/g, ' ')}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Object.entries(rows).map(([rowKey, rowData]) => (
          <TableRow key={rowKey} hover>
            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
              {rowKey.replace(/_/g, ' ')}
            </TableCell>
            {columns.map(colKey => (
              <EditableCell
                key={colKey}
                data={(rowData as { [key: string]: ValueObject })[colKey]}
                path={[...basePath, rowKey, colKey]}
              />
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
  );
});

export const ExtractedDataTable: React.FC = () => {
  const { t } = useTranslation();
  const { extractedData } = useOcrInputStore();

  const dynamicLayout = useMemo(() => {
    const data = extractedData?.[0];
    if (!data) return null;

    const simpleRows: { [key: string]: ValueObject } = {};
    const groupedTables = new Map<string, ExtractedData>();

    Object.entries(data)
      .filter(([key]) => key.trim() !== '')
      .forEach(([key, value]) => {
        if (isValueObject(value)) {
          simpleRows[key] = value;
        } else if (value && typeof value === 'object') {
          const structureSignature = JSON.stringify(Object.keys(value).sort());
          if (!groupedTables.has(structureSignature)) {
            groupedTables.set(structureSignature, {});
          }
          groupedTables.get(structureSignature)![key] = value;
        }
      });
    
    return { simpleRows, groupedTables };
  }, [extractedData]);

  if (!dynamicLayout) {
    return <Typography sx={{ p: 2 }}>{t('ocrDataInput.noDataToDisplay', 'No data available to display.')}</Typography>;
  }
  
  const basePath = [0];

  return (
    <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
      {Object.entries(dynamicLayout.simpleRows).map(([title, data]) => (
        <SimpleKeyValueTable key={title} title={title} data={data} basePath={basePath} />
      ))}

      {Array.from(dynamicLayout.groupedTables.entries()).map(([signature, rows]) => {
        const columnSet = new Set<string>();
        Object.values(rows).forEach(rowContent => {
          Object.keys(rowContent).forEach(colKey => columnSet.add(colKey));
        });
        const columns = Array.from(columnSet);
        
        return <ComplexTable key={signature} rows={rows} columns={columns} basePath={basePath} />;
      })}
    </Box>
  );
};